import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { FadeUp, LineReveal } from "@/components/site/Reveal";
import { CtaBand } from "@/components/site/CtaBand";
import { IMG, STEPS } from "@/lib/content";

const SOLUTIONS = [
  { id: "solar", kicker: "01 — Solar", title: "Commercial carwash solar", img: IMG.solarAerial, text: "Up to 100kW on a normal carwash roof without special engineering or energy redesigns — permanently wiping out around $6,000 of power costs every month for 25 years.", points: ["New, upgraded or add-on systems", "Sized to your wash and your bills", "Save 53%–87% on average"] },
  { id: "batteries", kicker: "02 — Batteries", title: "Battery storage", img: IMG.battery, text: "Add batteries to your current system or install them with new solar to reduce — and in many cases eliminate — your power costs. The federal Cheaper Solar Batteries Program covers 30% of battery system costs for businesses.", points: ["Add-on to existing solar or upsize by up to 200%", "Eligible systems 5–100 kWh", "Stackable with state incentives"], link: "/batteries" },
  { id: "leasing", kicker: "03 — Funding", title: "Green Operational Leasing", img: IMG.washSuds, text: "Prefer to pay nothing upfront? Redirect your power-bill savings into a monthly lease payment and keep the rest as bottom-line profit.", points: ["Zero capital outlay", "100% tax deductible", "No complex depreciation schedules"] },
];

const FUNDING = [
  { big: "$70K", label: "Per meter", text: "From June 2026, the Federal Government will pay you up to $70,000 per meter in rebates, subsidies and outright discounts on new, upgraded or add-on solar and/or batteries." },
  { big: "$0", label: "Upfront", text: "Green Operational Leasing lets your monthly savings fund your solar + battery system. Your bank is even rewarded for supporting customer solar lending." },
  { big: "Super", label: "Fund eligible", text: "Depending on your situation, your system can even be paid for by your super fund." },
];

const SolutionRow = ({ s, i }) => (
  <div data-testid={`solution-${s.id}`} className="grid lg:grid-cols-12 gap-8 lg:gap-14 items-center py-14 lg:py-20 border-t border-white/10">
    <FadeUp className={`lg:col-span-6 ${i % 2 ? "lg:order-2" : ""}`}>
      <div className="relative aspect-[16/11] overflow-hidden rounded-2xl group">
        <img src={s.img} alt={s.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] group-hover:scale-105" />
        <div className="absolute inset-0 bg-[var(--navy)]/25" />
      </div>
    </FadeUp>
    <FadeUp delay={0.1} className="lg:col-span-6">
      <p className="font-mono text-xs tracking-[0.2em] uppercase text-[var(--sun)]">{s.kicker}</p>
      <h3 className="mt-3 font-display font-black uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.9]">{s.title}</h3>
      <p className="mt-5 text-base sm:text-lg text-slate-300 leading-relaxed max-w-xl">{s.text}</p>
      <ul className="mt-6 space-y-2">
        {s.points.map((p) => (
          <li key={p} className="flex items-center gap-3 text-slate-200"><span className="h-2 w-2 rounded-full bg-[var(--sun)]" />{p}</li>
        ))}
      </ul>
      {s.link && (
        <Link to={s.link} data-testid={`solution-${s.id}-link`} className="mt-8 inline-flex items-center gap-2 font-display font-bold uppercase tracking-wider text-[var(--sun)] hover:text-white transition-colors">
          See the battery rebate <ArrowUpRight size={18} />
        </Link>
      )}
    </FadeUp>
  </div>
);

export default function Solutions() {
  return (
    <div data-testid="solutions-page">
      <PageHero
        testId="solutions-hero"
        eyebrow="Solutions"
        lines={["We remove", "the roadblocks."]}
        intro="Solar powering your carwash can be complex. Carwash Solar + Batteries removes the roadblocks, risks and upfront costs — easily and simply."
        image={IMG.solarAerial}
      />
      <section className="bg-[var(--ink)]">
        <div className="mx-auto max-w-[1440px] px-5 lg:px-10 pb-16">
          {SOLUTIONS.map((s, i) => <SolutionRow key={s.id} s={s} i={i} />)}
        </div>
      </section>
      <section data-testid="process-section" className="bg-[var(--concrete)] text-[var(--ink)]">
        <div className="mx-auto max-w-[1440px] px-5 lg:px-10 py-24 lg:py-32">
          <p className="eyebrow eyebrow-dark">How it works</p>
          <LineReveal onView lines={["Assess. Design. Install.", "Monitor. Maintain."]} className="mt-5 font-display font-black uppercase leading-[0.88] text-5xl sm:text-6xl lg:text-7xl" />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {STEPS.map((s, i) => (
              <FadeUp key={s.n} delay={i * 0.07} data-testid={`process-step-${s.n}`} className="group rounded-2xl bg-white border border-black/5 p-7 transition-colors duration-300 hover:bg-[var(--ink)] hover:text-white">
                <p className="font-display font-black text-6xl text-[var(--sun)] [-webkit-text-stroke:1px_var(--ink)] group-hover:[-webkit-text-stroke:0]">{s.n}</p>
                <h3 className="mt-6 font-display font-bold uppercase text-3xl">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600 group-hover:text-slate-300">{s.text}</p>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>
      <section data-testid="funding-section" className="bg-[var(--navy)]">
        <div className="mx-auto max-w-[1440px] px-5 lg:px-10 py-24 lg:py-32">
          <p className="eyebrow">Funding options</p>
          <LineReveal onView lines={["Whether you own,", "lease or rent."]} className="mt-5 font-display font-black uppercase leading-[0.88] text-5xl sm:text-6xl lg:text-7xl" />
          <div className="mt-14 grid md:grid-cols-3 gap-4">
            {FUNDING.map((f, i) => (
              <FadeUp key={f.label} delay={i * 0.08} className="rounded-2xl border border-white/10 bg-[var(--ink)] p-8">
                <p className="font-display font-black text-7xl text-[var(--sun)] leading-none">{f.big}</p>
                <p className="mt-2 font-mono text-xs tracking-[0.2em] uppercase text-[var(--solar-light)]">{f.label}</p>
                <p className="mt-5 text-slate-300 leading-relaxed">{f.text}</p>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>
      <CtaBand />
    </div>
  );
}
